import React, { FC, Fragment, useEffect, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { CheckIcon } from '@heroicons/react/outline';
import { EditListSortData, UpdateListSortProps } from '../../utility/types';
import { useMutation } from '@tanstack/react-query';
import { useUpdateListSort } from '../../hooks/api/ListSort/useUpdateListSort';

interface EditListSortModalProps {
  isOpen: boolean;
  onClose: () => void;
  listSortEditableProperties: {name: string, notes: string, list_upload_name: string, list_upload_id: string};
}

const EditListSortModal: FC<EditListSortModalProps> = ({
  isOpen,
  onClose,
  listSortEditableProperties,
}) => {
  const saveButtonRef = useRef(null);
  const [ formData, setFormData ] = useState(listSortEditableProperties);
  const [ isInvalidName, setIsInvalidName ] = useState(false);
  const { updateListSort, isSuccess, isError, isPending, error, response } = useUpdateListSort();

  // Use a useEffect to handle the modal closing logic
  useEffect(() => {
    if (isSuccess) {
      onClose();
    }
  }, [ isSuccess, onClose ]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    // changing e.target.name to e.target.value
    if (e.target.name === 'name') {
      setIsInvalidName(false);
    }
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      setIsInvalidName(true);
      return;
    }

    await updateListSort({ name: formData.name, notes: formData.notes, list_upload_id: formData.list_upload_id });
  };

  return (
    <>
      <Transition.Root show={isOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" initialFocus={saveButtonRef} onClose={onClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <div className="mt-3 text-center sm:mt-5">
                      <Dialog.Title as="h2" className="text-xl font-semibold mb-4">
                        Edit List Sort
                      </Dialog.Title>
                      <div className="bg-white p-4 rounded-md">
                        <form>
                          <div className="mb-4">
                            <label htmlFor="name" className="block text-gray-600">New List Sort Name</label>
                            {isInvalidName && <span style={{ color: 'red' }}>Name cannot be empty</span>}
                            <input
                              type="text"
                              id="name"
                              name="name"
                              value={formData.name}
                              onChange={handleInputChange}
                              className="w-full border rounded-md px-3 py-2"
                              required
                            />
                          </div>
                          <div className="mb-4">
                            <label htmlFor="notes" className="block text-gray-600">List Sort Notes (optional)
                            </label>
                            <textarea
                              id="notes"
                              name="notes"
                              value={formData.notes}
                              onChange={handleInputChange}
                              className="w-full border rounded-md px-3 py-2"
                            />
                          </div>
                          <div className="mb-4">
                            {listSortEditableProperties.list_upload_name ? (
                              <>
                                <label htmlFor="selectedListUpload" className="block text-gray-600">Cannot Change List Upload Source</label>
                                <textarea
                                  id="selectedListUpload"
                                  name="selectedListUpload"
                                  value={listSortEditableProperties.list_upload_name}
                                  className="w-full border rounded-md px-3 py-2"
                                  disabled
                                />
                              </>) : <div> No List Upload set with this List Sort. You cannot change this.</div>}
                          </div>
                        </form>
                      </div>
                    </div>
                  </div>
                  <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                    <button
                      type="button"
                      className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 sm:col-start-2"
                      onClick={handleSubmit}
                      ref={saveButtonRef}
                    >
                    Save
                    </button>
                    <button
                      type="button"
                      className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
                      onClick={onClose}
                    >
                    Cancel
                    </button>
                  </div>
                  {isError && <div> Something went wrong {isError}</div>}
                  {isPending && <div> Processing list sort edit</div>}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );
};

export default EditListSortModal;
